import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["modal", "searchInput", "mealtimeFilter", "results", "linkedRecipes", "emptyState"];
  static values = {
    customMenuId: Number,
    availableUrl: String,
    createUrl: String
  };

  connect() {
    this.searchTimeout = null;
  }

  openModal(event) {
    event.preventDefault();
    if (this.hasModalTarget && typeof $ !== 'undefined' && $.fn.modal) {
      this.modalTarget.removeAttribute('aria-hidden');
      $(this.modalTarget).modal('show');
      this.loadAvailableRecipes();
    }
  }

  closeModal() {
    if (this.hasModalTarget && typeof $ !== 'undefined' && $.fn.modal) {
      $(this.modalTarget).modal('hide');
    }
  }

  search() {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.loadAvailableRecipes();
    }, 300);
  }

  filterByMealtime() {
    this.loadAvailableRecipes();
  }

  async loadAvailableRecipes() {
    const query = this.hasSearchInputTarget ? this.searchInputTarget.value : '';
    const mealtime = this.hasMealtimeFilterTarget ? this.mealtimeFilterTarget.value : '';

    const params = new URLSearchParams();
    if (query) params.append('query', query);
    if (mealtime) params.append('mealtime', mealtime);

    const url = `${this.availableUrlValue}?${params.toString()}`;

    try {
      this.resultsTarget.innerHTML = '<div class="text-center py-4"><div class="spinner-border text-success" role="status"></div></div>';

      const response = await fetch(url, {
        headers: {
          'Accept': 'text/html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (response.ok) {
        const html = await response.text();
        this.resultsTarget.innerHTML = html;
      } else {
        this.resultsTarget.innerHTML = '<div class="alert alert-danger">Error al cargar recetas</div>';
      }
    } catch (error) {
      console.error('Error loading recipes:', error);
      this.resultsTarget.innerHTML = '<div class="alert alert-danger">Error de conexión</div>';
    }
  }

  async addRecipe(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const recipeId = button.dataset.recipeId;

    button.disabled = true;
    button.innerHTML = '<i class="fas fa-spinner fa-spin"></i>';

    try {
      const response = await fetch(this.createUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          custom_menu_recipe: { recipe_id: recipeId }
        })
      });

      if (response.ok) {
        const data = await response.json();
        this.appendLinkedRecipe(data);
        this.updateEmptyState();
        // Remove from available list
        const recipeCard = button.closest('.recipe-available-card');
        if (recipeCard) recipeCard.remove();
      } else {
        const error = await response.json();
        alert(error.errors?.join(', ') || 'Error al agregar receta');
        button.disabled = false;
        button.innerHTML = '<i class="fas fa-plus"></i>';
      }
    } catch (error) {
      console.error('Error adding recipe:', error);
      alert('Error de conexión');
      button.disabled = false;
      button.innerHTML = '<i class="fas fa-plus"></i>';
    }
  }

  async removeRecipe(event) {
    event.preventDefault();
    if (!confirm('¿Eliminar esta receta del menú?')) return;

    const button = event.currentTarget;
    const recipeCard = button.closest('.linked-recipe-card');
    const destroyUrl = button.dataset.destroyUrl;

    button.disabled = true;

    try {
      const response = await fetch(destroyUrl, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      });

      if (response.ok) {
        recipeCard.remove();
        this.updateEmptyState();
      } else {
        alert('Error al eliminar receta');
        button.disabled = false;
      }
    } catch (error) {
      console.error('Error removing recipe:', error);
      alert('Error de conexión');
      button.disabled = false;
    }
  }

  appendLinkedRecipe(data) {
    const recipe = data.recipe;
    const html = `
      <div class="col-md-4 col-lg-3 mb-3 linked-recipe-card" data-recipe-id="${recipe.id}">
        <div class="card h-100">
          <div class="card-img-top" style="height: 120px; background: linear-gradient(135deg, #33A036 0%, #2d8f30 100%); display: flex; align-items: center; justify-content: center;">
            <i class="fas fa-utensils text-white" style="font-size: 2rem;"></i>
          </div>
          <div class="card-body p-2">
            <h6 class="card-title mb-1 small font-weight-bold">${this.escapeHtml(recipe.name)}</h6>
            ${recipe.mealtime ? `<span class="badge badge-success badge-sm">${this.escapeHtml(recipe.mealtime)}</span>` : ''}
          </div>
          <div class="card-footer p-2 bg-white border-top-0">
            <div class="btn-group btn-group-sm w-100">
              <a href="/recipes/${recipe.id}" class="btn btn-outline-primary" target="_blank">
                <i class="fas fa-eye"></i>
              </a>
              ${recipe.published ? `<a href="/receta/${recipe.id}" class="btn btn-outline-success" target="_blank" title="Ver receta publica"><i class="fas fa-share-alt"></i></a>` : ''}
              <button type="button" class="btn btn-outline-danger"
                      data-action="click->custom-menu-recipes#removeRecipe"
                      data-destroy-url="/custom_menus/${this.customMenuIdValue}/custom_menu_recipes/${data.id}">
                <i class="fas fa-trash"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    `;

    if (this.hasLinkedRecipesTarget) {
      this.linkedRecipesTarget.insertAdjacentHTML('beforeend', html);
    }
  }

  updateEmptyState() {
    if (!this.hasEmptyStateTarget || !this.hasLinkedRecipesTarget) return;

    const hasRecipes = this.linkedRecipesTarget.querySelectorAll('.linked-recipe-card').length > 0;
    this.emptyStateTarget.style.display = hasRecipes ? 'none' : 'block';
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}
